import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import MarketingShell from "./MarketingShell";
import PricingCards from "@/components/PricingCards";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarClock, Clock, Globe2, Layers, LineChart, MailCheck, MousePointer2, Sparkles, Star, Users } from "lucide-react";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function Home() {
    const { t } = useI18n();
    const nav = useNavigate();
    const features = [
        { icon: CalendarClock, t: "marketing.f1t", d: "marketing.f1d" },
        { icon: Clock, t: "marketing.f2t", d: "marketing.f2d" },
        { icon: Globe2, t: "marketing.f3t", d: "marketing.f3d" },
        { icon: MailCheck, t: "marketing.f4t", d: "marketing.f4d" },
        { icon: Layers, t: "marketing.f5t", d: "marketing.f5d" },
        { icon: Sparkles, t: "marketing.f7t", d: "marketing.f7d" },
        { icon: MousePointer2, t: "marketing.f8t", d: "marketing.f8d" },
        { icon: LineChart, t: "marketing.f6t", d: "marketing.f6d" },
    ];
    return (
        <MarketingShell>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:grid-cols-2 md:py-24">
                    <motion.div {...fade}>
                        <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5 text-sm font-medium text-accent">
                            <Star className="h-3.5 w-3.5 fill-accent" /> {t("marketing.tagline")}
                        </span>
                        <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                            {t("marketing.heroTitle")}
                        </h1>
                        <p className="mt-6 max-w-lg text-base text-muted-foreground md:text-lg">{t("marketing.heroSub")}</p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <Button size="lg" data-testid="hero-trial-btn" onClick={() => nav("/register")} className="rounded-full px-8 text-base">
                                {t("marketing.heroCta")} <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Link to="/features"><Button size="lg" variant="outline" className="rounded-full px-8 text-base">{t("marketing.heroCta2")}</Button></Link>
                        </div>
                        <p className="mt-4 text-sm text-muted-foreground">{t("marketing.trialNote")}</p>
                    </motion.div>
                    <motion.div {...fade} className="relative">
                        <div className="absolute -inset-4 rounded-[2rem] bg-accent/10 blur-2xl" />
                        <img
                            src="https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6"
                            alt="Modern salon"
                            className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-[0_24px_64px_rgba(0,0,0,0.16)] max-h-[80vh] md:max-h-none"
                        />
                        <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/20 bg-white/80 p-4 backdrop-blur-xl">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-1.5 rounded-full" style={{ background: "#4C6EF5" }} />
                                <div>
                                    <p className="text-sm font-semibold text-neutral-900">Emma Janssens · Knippen</p>
                                    <p className="text-xs text-neutral-500">10:03 – 10:47 · Lisa</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FEATURES bento */}
            <section className="mx-auto max-w-7xl px-6 py-20">
                <motion.div {...fade} className="max-w-2xl">
                    <h2 className="font-serif text-3xl font-bold tracking-tight md:text-4xl">{t("marketing.featuresTitle")}</h2>
                    <p className="mt-3 text-muted-foreground">{t("marketing.featuresSub")}</p>
                </motion.div>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {features.map((f, i) => (
                        <motion.div key={i} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }}
                            className={`group rounded-2xl border border-border/70 bg-card p-8 transition-transform duration-200 hover:-translate-y-1 ${i === 0 ? "md:col-span-2" : ""}`}>
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/12 text-accent transition-colors duration-200 group-hover:bg-accent group-hover:text-accent-foreground">
                                <f.icon className="h-6 w-6" />
                            </div>
                            <h3 className="mt-5 text-lg font-semibold">{t(f.t)}</h3>
                            <p className="mt-2 text-sm text-muted-foreground">{t(f.d)}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* PRICING */}
            <section className="bg-secondary/40 py-20">
                <div className="mx-auto max-w-5xl px-6">
                    <motion.div {...fade} className="mb-12 text-center">
                        <h2 className="font-serif text-3xl font-bold tracking-tight md:text-4xl">{t("marketing.pricingTitle")}</h2>
                        <p className="mt-3 text-muted-foreground">{t("marketing.pricingSub")}</p>
                    </motion.div>
                    <PricingCards />
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-6 py-20">
                <div className="relative overflow-hidden rounded-[2rem] bg-primary px-8 py-16 text-center text-primary-foreground md:px-16">
                    <div className="grain absolute inset-0" />
                    <h2 className="relative font-serif text-3xl font-bold tracking-tight md:text-4xl">{t("marketing.ctaBottomTitle")}</h2>
                    <p className="relative mx-auto mt-3 max-w-lg text-primary-foreground/70">{t("marketing.ctaBottomSub")}</p>
                    <Button size="lg" data-testid="cta-trial-btn" onClick={() => nav("/register")} className="relative mt-8 rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
                        {t("marketing.heroCta")} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            </section>
        </MarketingShell>
    );
}
